/**
 * Role-branched registration statechart for the BluePages `/register` wizard.
 *
 * The step-1 role is the only branch authority: `employer` runs the
 * company-details flow, `candidate`/`both` run the candidate flow, and a
 * missing/unknown role routes to `roleError` — never a silent candidate
 * fallback.
 *
 * @see Documentation/Specification/04_Processes/application-specific/workseeking/CandidateAndEmployerRegistration.md
 *   §2 (branch authority), §3 (step matrix), §4 (company fields)
 * @see Documentation/Specification/020_Definitions/application-agnostic/auth/Register.md
 */
import { setup, assign } from 'xstate';

export type RegistrationRole = 'candidate' | 'employer' | 'both';
export type ProfileType = 'Individual' | 'Organisation';

export interface AccountData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface RegistrationContext {
  role: RegistrationRole | null;
  account: AccountData | null;
  profileType: ProfileType | null;
  nationalId: { type: string; number: string; country: string } | null;
  headline: string;
  availability: string;
  workType: string;
  province: string;
  city: string;
  skills: string[];
  companyName: string;
  industry: string;
  companySize: string;
  submissionError: string | null;
}

export const emptyRegistrationContext: RegistrationContext = {
  role: null,
  account: null,
  profileType: null,
  nationalId: null,
  headline: '',
  availability: '',
  workType: 'Contract',
  province: '',
  city: '',
  skills: [],
  companyName: '',
  industry: '',
  companySize: '1-10',
  submissionError: null,
};

export type RegistrationEvent =
  | { type: 'START' }
  | { type: 'SELECT_ROLE'; role: RegistrationRole }
  | { type: 'BACK' }
  | { type: 'SUBMIT_ACCOUNT'; account: AccountData }
  | { type: 'SELECT_PROFILE_TYPE'; profileType: ProfileType }
  | {
      type: 'SUBMIT_PROFILE';
      headline: string;
      availability: string;
      workType: string;
      province: string;
      city: string;
    }
  | { type: 'SUBMIT_NATIONAL_ID'; nationalId: { type: string; number: string; country: string } | null }
  | { type: 'ADD_SKILLS'; skills: string[] }
  | { type: 'SKIP_SKILLS' }
  | { type: 'SUBMIT_COMPANY'; companyName: string; industry?: string; companySize?: string }
  | { type: 'EDIT_ROLE' }
  | { type: 'EDIT_ACCOUNT' }
  | { type: 'EDIT_PROFILE' }
  | { type: 'EDIT_NATIONAL_ID' }
  | { type: 'EDIT_SKILLS' }
  | { type: 'EDIT_COMPANY' }
  | { type: 'SUBMIT' }
  | { type: 'SUBMITTED' }
  | { type: 'ERROR'; message?: string };

export const registrationMachine = setup({
  types: {
    context: {} as RegistrationContext,
    events: {} as RegistrationEvent,
  },
  guards: {
    /** Employer flow — company-details branch (spec §2 rule 1). */
    isEmployer: ({ context }) => context.role === 'employer',
    /** Candidate flow present for `candidate` and `both` (spec §3). */
    isCandidateFlow: ({ context }) => context.role === 'candidate' || context.role === 'both',
    /** `both` collects candidate AND company data (spec §3). */
    isBoth: ({ context }) => context.role === 'both',
    /** Company-details step belongs after skills for `employer`/`both` (spec §3). */
    needsCompanyDetails: ({ context }) => context.role === 'employer' || context.role === 'both',
  },
}).createMachine({
  id: 'registration',
  context: emptyRegistrationContext,
  initial: 'idle',
  states: {
    idle: {
      on: { START: 'roleSelection' },
    },
    roleSelection: {
      on: {
        SELECT_ROLE: {
          target: 'accountDetails',
          actions: assign({
            role: ({ event }) => event.role,
          }),
        },
        BACK: 'idle',
      },
    },
    accountDetails: {
      on: {
        // Branch authority: step-1 role only. Unknown role -> roleError,
        // never a silent candidate fallback (spec §2 rule 4).
        SUBMIT_ACCOUNT: [
          {
            target: 'nationalId',
            guard: 'isEmployer',
            actions: assign({
              account: ({ event }) => event.account,
            }),
          },
          {
            target: 'profileType',
            guard: 'isCandidateFlow',
            actions: assign({
              account: ({ event }) => event.account,
            }),
          },
          {
            target: 'roleError',
            actions: assign({
              account: ({ event }) => event.account,
            }),
          },
        ],
        BACK: 'roleSelection',
      },
    },
    profileType: {
      on: {
        SELECT_PROFILE_TYPE: {
          target: 'basicProfile',
          actions: assign({
            profileType: ({ event }) => event.profileType,
          }),
        },
        BACK: 'accountDetails',
      },
    },
    basicProfile: {
      on: {
        SUBMIT_PROFILE: {
          target: 'nationalId',
          actions: assign({
            headline: ({ event }) => event.headline,
            availability: ({ event }) => event.availability,
            workType: ({ event }) => event.workType,
            province: ({ event }) => event.province,
            city: ({ event }) => event.city,
          }),
        },
        BACK: 'profileType',
      },
    },
    nationalId: {
      on: {
        SUBMIT_NATIONAL_ID: [
          {
            target: 'skillSelection',
            guard: 'isCandidateFlow',
            actions: assign({
              nationalId: ({ event }) => event.nationalId,
            }),
          },
          {
            target: 'companyDetails',
            guard: 'isEmployer',
            actions: assign({
              nationalId: ({ event }) => event.nationalId,
            }),
          },
          {
            target: 'roleError',
            actions: assign({
              nationalId: ({ event }) => event.nationalId,
            }),
          },
        ],
        // Employer entered nationalId from accountDetails — go back there.
        BACK: [
          { target: 'basicProfile', guard: 'isCandidateFlow' },
          { target: 'accountDetails' },
        ],
      },
    },
    skillSelection: {
      on: {
        ADD_SKILLS: [
          {
            target: 'companyDetails',
            guard: 'isBoth',
            actions: assign({ skills: ({ event }) => event.skills }),
          },
          {
            target: 'review',
            actions: assign({ skills: ({ event }) => event.skills }),
          },
        ],
        SKIP_SKILLS: [
          { target: 'companyDetails', guard: 'isBoth' },
          { target: 'review' },
        ],
        BACK: 'nationalId',
      },
    },
    companyDetails: {
      on: {
        SUBMIT_COMPANY: {
          target: 'review',
          actions: assign({
            companyName: ({ event }) => event.companyName,
            industry: ({ event }) => event.industry ?? '',
            companySize: ({ event }) => event.companySize ?? '1-10',
          }),
        },
        BACK: [
          { target: 'skillSelection', guard: 'isCandidateFlow' },
          { target: 'nationalId' },
        ],
      },
    },
    review: {
      on: {
        EDIT_ROLE: 'roleSelection',
        EDIT_ACCOUNT: 'accountDetails',
        EDIT_PROFILE: 'basicProfile',
        EDIT_NATIONAL_ID: 'nationalId',
        EDIT_SKILLS: 'skillSelection',
        EDIT_COMPANY: { target: 'companyDetails', guard: 'needsCompanyDetails' },
        SUBMIT: 'submitting',
        BACK: [
          { target: 'companyDetails', guard: 'needsCompanyDetails' },
          { target: 'skillSelection' },
        ],
      },
    },
    // Spec §2 rule 4: a missing or unknown role is a hard stop, not a
    // fallback into the candidate flow.
    roleError: {
      on: {
        SELECT_ROLE: {
          target: 'accountDetails',
          actions: assign({
            role: ({ event }) => event.role,
          }),
        },
        BACK: 'roleSelection',
      },
    },
    submitting: {
      on: {
        SUBMITTED: 'complete',
        ERROR: {
          target: 'review',
          actions: assign({
            submissionError: ({ event }) => event.message ?? 'Registration failed',
          }),
        },
      },
    },
    complete: {
      type: 'final',
    },
  },
});

export const registrationSteps = [
  'roleSelection',
  'accountDetails',
  'profileType',
  'basicProfile',
  'nationalId',
  'skillSelection',
  'companyDetails',
  'review',
] as const;

/**
 * Visible wizard steps for a selected role — step matrix per spec §3.
 * A null role previews the candidate flow (the stepper shows before the
 * role pick; it re-renders once a role is selected).
 *
 * @see Documentation/Specification/04_Processes/application-specific/workseeking/CandidateAndEmployerRegistration.md §3
 */
export function visibleStepsFor(role: RegistrationRole | null): string[] {
  const steps: string[] = ['roleSelection', 'accountDetails'];
  if (role !== 'employer') steps.push('profileType', 'basicProfile');
  steps.push('nationalId');
  if (role !== 'employer') steps.push('skillSelection');
  if (role === 'employer' || role === 'both') steps.push('companyDetails');
  steps.push('review');
  return steps;
}
